import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { StatusDot } from '@/components/ui/StatusDot'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { dateBR } from '@/lib/format'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { DiscountCouponsService, type DiscountCoupon } from '@/services/modules/DiscountCouponsService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'

export function DiscountCouponsListPage() {
  const navigate = useNavigate()
  const { can } = useAuthority()
  const canCreate = can('commercial.discount_coupons.create')
  const canEdit = can('commercial.discount_coupons.edit')
  const canDelete = can('commercial.discount_coupons.delete')
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar por código' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(DiscountCouponsService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<DiscountCoupon | null>(null)

  const exp = useDataTableExport({
    filename: 'cupons',
    service: DiscountCouponsService,
    filters: sf.filters,
    columns: [
      { header: 'Código',     accessor: (c: DiscountCoupon) => c.code },
      { header: 'Tipo',       accessor: (c: DiscountCoupon) => c.discountType },
      { header: 'Valor',      accessor: (c: DiscountCoupon) => c.formatted?.value ?? c.value },
      { header: 'Parcelas',   accessor: (c: DiscountCoupon) => c.monthlyInstallments == null ? 'Sem limite' : `${c.monthlyInstallments}x` },
      { header: 'Início',     accessor: (c: DiscountCoupon) => c.formatted?.startDate ?? '' },
      { header: 'Fim',        accessor: (c: DiscountCoupon) => c.formatted?.endDate ?? '' },
      { header: 'Planos',     accessor: (c: DiscountCoupon) => (c.plans ?? []).map(p => p.name).join(', ') || 'Todos' },
      { header: 'Status',     accessor: (c: DiscountCoupon) => c.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try {
      await DiscountCouponsService.destroy(openDelete.id)
      toast.success({ title: 'Removido', description: 'Cupom removido com sucesso.' })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<DiscountCoupon>[] = [
    { key: 'code',  header: 'Código', sortable: true, alwaysVisible: true, className: 'w-44',
      render: c => <span className="font-medium text-gray-800">{c.code}</span> },
    { key: 'discountType', header: 'Tipo', align: 'center', className: 'w-28',
      render: c => <span className="text-gray-600">{c.discountType === 'PORCENTAGEM' ? 'Porcentagem' : 'Valor fixo'}</span> },
    { key: 'value', header: 'Valor', align: 'center', className: 'w-28',
      render: c => <span className="font-medium text-gray-800">{c.formatted?.value ?? c.value}</span> },
    { key: 'installments', header: 'Parcelas', align: 'center', className: 'w-24',
      render: c => c.monthlyInstallments == null
        ? <span className="text-xs text-gray-500">Sem limite</span>
        : `${c.monthlyInstallments}x` },
    { key: 'plans', header: 'Planos', align: 'center', className: 'w-28',
      render: c => {
        const plans = c.plans ?? []
        if (plans.length === 0) return <span className="text-gray-400 italic text-xs">Todos</span>
        return (
          <InfoPopover
            align="center"
            ariaLabel="Ver planos do cupom"
            title="Planos do cupom"
            trigger={
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100 px-2 py-0.5 text-xs font-medium hover:bg-brand-100 transition">
                {plans.length} plano{plans.length === 1 ? '' : 's'}
              </span>
            }
          >
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {[...plans]
                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                .map(p => <li key={p.id} className="text-[13px] text-gray-700">{p.name}</li>)}
            </ul>
          </InfoPopover>
        )
      } },
    { key: 'period', header: 'Vigência', align: 'center', className: 'w-48',
      render: c => {
        const s = c.startDate ? dateBR(c.startDate) : null
        const e = c.endDate ? dateBR(c.endDate) : null
        if (!s && !e) return <span className="text-xs text-gray-400 italic">Sem vigência</span>
        return <span className="text-xs text-gray-600 whitespace-nowrap">{s ?? '∞'} <span className="text-gray-400">→</span> {e ?? '∞'}</span>
      } },
    { key: 'status', header: 'Status', align: 'center', className: 'w-20',
      render: c => <StatusDot value={c.status} /> },
  ]

  return (
    <>
      <PageHeader
        title="Cupons de desconto"
        subtitle="Cupons aplicáveis em planos selecionados"
        crumbs={[{ label: 'Comercial' }, { label: 'Cupons' }]}
        actions={canCreate ? <Button onClick={() => navigate('/commercial/discount-coupons/novo')} iconLeft={<Plus size={14} />}>Novo cupom</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="discountcoupons.list"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(c) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/commercial/discount-coupons/${c.id}/editar`) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(c) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover cupom?"
        description={`Tem certeza que deseja remover "${openDelete?.code}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
